import React, { useState } from "react";
import { motion } from "framer-motion";
import { AlertCircle, RotateCcw, ShieldAlert, Award, ArrowRight } from "lucide-react";
import { MIN_KARMA } from "./constants";

interface ErrorStateProps {
  username: string;
  errorMessage: string;
  isApiError: boolean;
  onReset: () => void;
  onManualOverride: (manualKarma: number) => void;
}

export default function ErrorState({
  username,
  errorMessage,
  isApiError,
  onReset,
  onManualOverride,
}: ErrorStateProps) {
  const [manualKarma, setManualKarma] = useState("");
  const [inputError, setInputError] = useState("");

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseInt(manualKarma, 10);
    if (isNaN(parsed) || parsed < 0) {
      setInputError("Please enter a valid karma score.");
      return;
    }
    if (parsed < MIN_KARMA) {
      setInputError(`Karma must be ${MIN_KARMA} or higher to be eligible.`);
      return;
    }
    setInputError("");
    onManualOverride(parsed);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="w-full max-w-lg mx-auto bg-white border-2 border-indigo-100 rounded-3xl p-8 text-slate-800 shadow-2xl shadow-indigo-100/50 relative overflow-hidden"
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-8 pb-6 border-b border-slate-100">
        <div className="flex items-center gap-2">
          {isApiError ? (
            <AlertCircle className="w-6 h-6 text-amber-600" />
          ) : (
            <ShieldAlert className="w-6 h-6 text-red-650" />
          )}
          <span className="font-black text-lg tracking-wide text-[#3F51B5]">
            {isApiError ? "Verification Error" : "Not Eligible"}
          </span>
        </div>
        <span
          className={`text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full border ${
            isApiError
              ? "text-amber-800 bg-amber-100/80 border-amber-300"
              : "text-red-800 bg-red-100/80 border-red-300"
          }`}
        >
          {isApiError ? "Verification Failed" : "Not Eligible"}
        </span>
      </div>

      {!isApiError ? (
        // Not eligible — covers both "low karma" and "account not found".
        // Username-only messaging; no karma numbers are ever displayed.
        <div className="text-center py-4">
          <div className="w-16 h-16 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-6">
            <ShieldAlert className="w-8 h-8 text-red-600" />
          </div>

          <h4 className="text-xl font-black mb-3 text-slate-900">
            u/{username} is not eligible
          </h4>
          <p className="text-slate-600 text-sm max-w-sm mx-auto mb-8 leading-relaxed font-semibold">
            The App20 Creator Program is reserved for active Reddit profiles that meet our
            minimum Karma requirement. This account doesn't qualify right now.
          </p>

          <button
            onClick={onReset}
            className="w-full py-4 px-6 rounded-full font-extrabold border-2 border-[#3F51B5] text-[#3F51B5] hover:bg-indigo-50 transition-all duration-300 flex items-center justify-center gap-2 text-md"
          >
            <RotateCcw className="w-4.5 h-4.5" />
            Try Different Account
          </button>
        </div>
      ) : (
        // Connection error - fallback to manual input
        <div>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex gap-3 text-sm">
            <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-amber-900 leading-relaxed font-bold">
              We couldn't verify <strong className="text-[#3F51B5]">u/{username}</strong> automatically.
              No worries! You can self-declare your Karma ({MIN_KARMA}+ required) to proceed. We'll
              double-check it before approval.
            </div>
          </div>

          <form onSubmit={handleManualSubmit} className="space-y-5">
            <div>
              <label className="block text-slate-600 text-xs font-bold uppercase tracking-wider mb-2">
                What is your current Reddit Karma?
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={manualKarma}
                  onChange={(e) => setManualKarma(e.target.value)}
                  placeholder={`e.g. ${MIN_KARMA}`}
                  className="w-full bg-slate-50 border-2 border-slate-200 px-4 py-4 rounded-2xl text-lg font-bold focus:outline-none focus:border-[#3F51B5] transition-all text-slate-900 placeholder-slate-400"
                />
                <Award className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-[#FFC107]" />
              </div>
              {inputError && <p className="text-red-600 text-xs mt-1.5 font-bold">{inputError}</p>}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={onReset}
                className="flex-1 py-4 px-6 rounded-full font-extrabold border-2 border-[#3F51B5] text-[#3F51B5] hover:bg-indigo-50 transition-all duration-300 flex items-center justify-center gap-2 text-md"
              >
                <RotateCcw className="w-4 h-4" />
                Go Back
              </button>

              <button
                type="submit"
                className="flex-[2] py-4 px-6 rounded-full font-black bg-gradient-to-r from-[#FFC107] to-[#FFB300] hover:from-[#FFD54F] hover:to-[#FFC107] text-black shadow-lg shadow-amber-400/25 transition-all duration-300 flex items-center justify-center gap-2 text-md"
              >
                Proceed With Form
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>
      )}
    </motion.div>
  );
}
